import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createLeague, DEFAULT_POINTS_CONFIG } from '../services/leagueService';
import './CreateLeagueModal.css';

const ROUNDS = [
  { key: 'group', label: 'Group Stage' },
  { key: 'r32',   label: 'Round of 32' },
  { key: 'r16',   label: 'Round of 16' },
  { key: 'qf',    label: 'Quarterfinal' },
  { key: 'sf',    label: 'Semifinal' },
  { key: 'final', label: 'Final' },
  { key: 'third', label: '3rd Place' },
];

export default function CreateLeagueModal({ onClose }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [showCustomPoints, setShowCustomPoints] = useState(false);
  const [pointsConfig, setPointsConfig] = useState({ ...DEFAULT_POINTS_CONFIG });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function handlePointChange(round, raw) {
    const val = parseFloat(raw);
    if (raw === '' || (!isNaN(val) && val >= 0)) {
      setPointsConfig((prev) => ({ ...prev, [round]: raw === '' ? '' : val }));
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!name.trim()) { setError('League name is required.'); return; }

    // Validate points: all must be non-negative numbers
    for (const { key } of ROUNDS) {
      const v = parseFloat(pointsConfig[key]);
      if (isNaN(v) || v < 0) {
        setError(`Points for "${ROUNDS.find(r => r.key === key)?.label}" must be a valid number.`);
        return;
      }
    }

    setLoading(true);
    setError('');
    try {
      // Ensure all values are numbers before saving
      const resolvedConfig = Object.fromEntries(
        ROUNDS.map(({ key }) => [key, parseFloat(pointsConfig[key]) || 0])
      );
      const leagueId = await createLeague(currentUser.uid, {
        name: name.trim(),
        isPublic,
        pointsConfig: resolvedConfig,
      });
      onClose();
      navigate(`/league/${leagueId}`);
    } catch (_) {
      setError('Failed to create league. Please try again.');
      setLoading(false);
    }
  }

  // Close on overlay click
  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-card" role="dialog" aria-modal="true" aria-labelledby="modal-title-id">
        <div className="modal-header">
          <h2 className="modal-title" id="modal-title-id">Create League</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <form className="modal-body" onSubmit={handleCreate} noValidate>
          {error && <div className="modal-error">{error}</div>}

          {/* League Name */}
          <div className="modal-field">
            <label className="modal-label" htmlFor="league-name">League Name</label>
            <input
              id="league-name"
              className="modal-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. The Champions Pool"
              maxLength={60}
              autoFocus
              required
            />
          </div>

          {/* Public / Private */}
          <div className="modal-field">
            <label className="modal-label">Visibility</label>
            <div className="visibility-toggle" role="group" aria-label="League visibility">
              <button
                type="button"
                className={`vis-btn${!isPublic ? ' active' : ''}`}
                onClick={() => setIsPublic(false)}
                aria-pressed={!isPublic}
              >
                Private
              </button>
              <button
                type="button"
                className={`vis-btn${isPublic ? ' active' : ''}`}
                onClick={() => setIsPublic(true)}
                aria-pressed={isPublic}
              >
                Public
              </button>
            </div>
            <p className="modal-hint">
              {isPublic
                ? 'Anyone can find and join from the public league directory.'
                : 'Members can only join via your invite link.'}
            </p>
            <p className="modal-hint modal-hint--warn">This cannot be changed after creation.</p>
          </div>

          {/* Custom Points */}
          <div className="modal-field">
            <button
              type="button"
              className="custom-points-toggle"
              onClick={() => setShowCustomPoints((v) => !v)}
              aria-expanded={showCustomPoints}
            >
              <span>Customize Points (Optional)</span>
              <span className={`chevron${showCustomPoints ? ' open' : ''}`}>▾</span>
            </button>

            {showCustomPoints && (
              <div className="custom-points-body">
                <p className="modal-hint" style={{ marginBottom: 8 }}>
                  Unique correct pick bonus = 50% of the round's base points (automatic, not configurable).
                </p>
                {ROUNDS.map(({ key, label }) => {
                  const base = parseFloat(pointsConfig[key]) || 0;
                  return (
                    <div className="points-row" key={key}>
                      <span className="points-label">{label}</span>
                      <div className="points-input-wrap">
                        <input
                          className="points-input"
                          type="number"
                          min="0"
                          step="1"
                          value={pointsConfig[key]}
                          onChange={(e) => handlePointChange(key, e.target.value)}
                          aria-label={`${label} points`}
                        />
                        <span className="points-unit">pts</span>
                      </div>
                      <span className="points-bonus">+{base * 0.5} unique</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <button
            type="submit"
            className="modal-btn-create"
            disabled={loading || !name.trim()}
          >
            {loading ? 'Creating…' : 'Create League'}
          </button>
        </form>
      </div>
    </div>
  );
}
